// /app/frontend/src/hooks/useSounds.js
// Centralized sound and vibration management for ADE app

// Sound cache to avoid recreating Audio objects
const soundCache = {};

// Preload sounds
const SOUNDS = {
  refresh: '/sounds/refresh.wav',
  gift: '/sounds/gift.wav',
  message: '/sounds/message.wav',
  connection: '/sounds/connection.wav',
  request: '/sounds/request.wav',
};

// Default volumes for each sound type
const VOLUMES = {
  refresh: 0.3,
  gift: 0.5,
  message: 0.4,
  connection: 0.5,
  request: 0.4,
};

// Vibration patterns (in milliseconds)
// [vibrate, pause, vibrate, pause, ...]
const VIBRATION_PATTERNS = {
  refresh: [50],                    // Short pulse
  gift: [100, 50, 100, 50, 200],   // Celebration pattern
  message: [100, 50, 100],          // Double pulse
  connection: [200, 100, 200],      // Strong double pulse
  request: [150, 75, 150],          // Alert pattern
};

/**
 * Vibrate the device
 * @param {string} patternName - Name of the vibration pattern
 */
export const vibrate = (patternName) => {
  try {
    if ('vibrate' in navigator) {
      const pattern = VIBRATION_PATTERNS[patternName] || [100];
      navigator.vibrate(pattern);
    }
  } catch (e) {
    // Vibration not supported or error, ignore
  }
};

/**
 * Play a sound effect
 * @param {string} soundName - Name of the sound (refresh, gift, message, connection, request)
 * @param {number} volume - Optional volume override (0-1)
 */
export const playSound = (soundName, volume = null) => {
  try {
    if (!SOUNDS[soundName]) {
      console.warn(`Sound "${soundName}" not found`);
      return;
    }
    
    // Get or create cached audio instance
    if (!soundCache[soundName]) {
      soundCache[soundName] = new Audio(SOUNDS[soundName]);
    }
    
    const audio = soundCache[soundName];
    audio.volume = volume ?? VOLUMES[soundName] ?? 0.3;
    audio.currentTime = 0;
    
    // Play and ignore autoplay restrictions
    audio.play().catch(() => {
      // Autoplay blocked, ignore silently
    });
  } catch (e) {
    // Sound not available or error, ignore
    console.warn('Sound playback failed:', e);
  }
};

/**
 * Play sound AND vibrate for important notifications
 * @param {string} name - Name of the sound/vibration pattern
 * @param {boolean} shouldVibrate - Whether to also vibrate (default: true)
 */
export const notify = (name, shouldVibrate = true) => {
  playSound(name);
  if (shouldVibrate) {
    vibrate(name);
  }
};

/**
 * Preload all sounds for faster playback
 */
export const preloadSounds = () => {
  Object.entries(SOUNDS).forEach(([name, path]) => {
    try {
      soundCache[name] = new Audio(path);
      soundCache[name].load();
    } catch (e) {
      // Ignore preload errors
    }
  });
};

/**
 * React hook for sound and vibration effects
 */
export const useSounds = () => {
  return {
    playRefresh: () => playSound('refresh'),
    playGift: () => notify('gift'),
    playMessage: () => notify('message'),
    playConnection: () => notify('connection'),
    playRequest: () => notify('request'),
    play: playSound,
    vibrate,
    notify,
  };
};

export default useSounds;
